import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check, Film, MessageSquare, Trophy, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 45_000;

const KIND_ICON = {
  invite_signup: UserPlus,
  award_won: Trophy,
  event_rsvp: Film,
  post_comment: MessageSquare,
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const panelRef = useRef(null);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.count ?? 0);
    } catch { /* ignore — user may be logged out */ }
  };

  const fetchList = async () => {
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setUnread((c) => Math.max(0, c - 1));
        setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch { /* ignore */ }
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const markAll = async () => {
    try {
      const { data } = await api.post("/notifications/read-all");
      setUnread(0);
      setItems((arr) => arr.map((x) => ({ ...x, read: true })));
      if (data?.updated > 0) toast.success(`Marked ${data.updated} as read`);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-cream-surface transition-colors text-jjblue"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        data-testid="notif-bell"
      >
        <Bell className="h-5 w-5" strokeWidth={1.6} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-burgundy text-cream text-[10px] font-semibold flex items-center justify-center border-2 border-cream"
            data-testid="notif-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] jj-card overflow-hidden z-50 shadow-2xl"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-jj">
            <div className="font-serif text-lg text-jjblue">Notifications</div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-burgundy hover:underline" data-testid="notif-mark-all">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-[var(--jj-muted)] hover:text-burgundy" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-[var(--jj-muted)]">
                <Bell className="h-7 w-7 mx-auto text-burgundy/40 mb-3" strokeWidth={1.4} />
                You&rsquo;re all caught up. Joy moments will land here.
              </div>
            ) : (
              items.map((n) => {
                const Icon = KIND_ICON[n.kind] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => markRead(n)}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-jj/40 last:border-b-0 transition-colors ${
                      n.read ? "bg-cream" : "bg-gold/[0.08] hover:bg-gold/[0.14]"
                    }`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className={`mt-0.5 h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                      n.read ? "bg-cream-surface text-burgundy/60" : "bg-burgundy text-cream"
                    }`}>
                      <Icon className="h-4 w-4" strokeWidth={1.6} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm leading-snug ${n.read ? "text-jjblue/80" : "text-jjblue font-medium"}`}>
                        {n.title}
                      </div>
                      {n.body && (
                        <div className="mt-0.5 text-xs text-[var(--jj-muted)] line-clamp-2">{n.body}</div>
                      )}
                      <div className="mt-1 text-[10px] uppercase tracking-wider text-[var(--jj-muted)]">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && (
                      <span className="mt-2 h-2 w-2 rounded-full bg-burgundy shrink-0" aria-label="unread" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {items.length > 0 && (
            <div className="px-4 py-2 border-t border-jj bg-cream-surface text-[11px] text-[var(--jj-muted)] flex items-center gap-1.5">
              <Check className="h-3 w-3" /> Updates every 45s
            </div>
          )}
        </div>
      )}
    </div>
  );
}
